import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format, subDays, previousWednesday, isWednesday } from 'date-fns';
import {
  csatPull, csatAnalyzeBatch, csatExport, csatAnalyzeEmail, getAgents,
} from '../api/scout';
import { LoadingOverlay, Spinner } from '../components/LoadingState';
import CategoryBadge from '../components/CategoryBadge';
import SeverityBadge from '../components/SeverityBadge';
import { useTheme, ACCENT } from '../contexts/ThemeContext';

function getLastWednesday() {
  const now = new Date();
  if (isWednesday(now)) return format(subDays(now, 7), 'yyyy-MM-dd');
  return format(previousWednesday(now), 'yyyy-MM-dd');
}

function getThisWednesday() {
  const now = new Date();
  if (isWednesday(now)) return format(now, 'yyyy-MM-dd');
  // Find next Wednesday from last Wednesday + 7
  const last = previousWednesday(now);
  const next = new Date(last);
  next.setDate(next.getDate() + 7);
  return format(next, 'yyyy-MM-dd');
}

const SUB_CAT_COLORS = {
  'Feature Functionality': '#3B82F6',
  'Product Accessories': '#8B5CF6',
  'Quality Control': '#EF4444',
  'Feature Request': '#06B6D4',
  'Product Limitation': '#F59E0B',
  'Data Access': '#10B981',
  'Fulfillment Issue': '#F97316',
  'Process Delay': '#EF4444',
  'Order Action Window': '#8B5CF6',
  'Out of Warranty': '#6B7280',
  'Troubleshooting Steps': '#3B82F6',
  'Policy Disagreement': '#F59E0B',
  'No Customer Response': '#6B7280',
  'Wrong Language': '#EF4444',
  'Unhappy with response': '#F97316',
  'Misinformation': '#EF4444',
  'AI Prejudice': '#F59E0B',
  'System/escalation error': '#8B5CF6',
};

export default function CSATReview() {
  const { t } = useTheme();
  const [tab, setTab] = useState('batch'); // 'batch' | 'email'
  const [startDate, setStartDate] = useState(getLastWednesday());
  const [endDate, setEndDate] = useState(getThisWednesday());
  const [assignee, setAssignee] = useState('');
  const [agents, setAgents] = useState([]);
  const [pulling, setPulling] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('pull'); // 'pull' | 'select' | 'results'
  const [emailText, setEmailText] = useState('');
  const [emailResult, setEmailResult] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Ben's wishlist filters
  const [filterCountry, setFilterCountry] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterRMA, setFilterRMA] = useState('');
  const [filterRRP, setFilterRRP] = useState('');

  // Compute filtered results
  const filteredResults = React.useMemo(() => {
    if (!results) return [];
    return results.filter(r => {
      if (filterCountry && r.activateCountry !== filterCountry) return false;
      if (filterLanguage && r.language !== filterLanguage) return false;
      if (filterRMA && r.rmaGroup !== filterRMA) return false;
      if (filterRRP && r.rrpTag !== filterRRP) return false;
      return true;
    });
  }, [results, filterCountry, filterLanguage, filterRMA, filterRRP]);

  // Unique values for filter dropdowns
  const filterOptions = React.useMemo(() => {
    if (!results) return { countries: [], languages: [], rmaGroups: [], rrpTags: [] };
    const countries = [...new Set(results.map(r => r.activateCountry).filter(Boolean))].sort();
    const languages = [...new Set(results.map(r => r.language).filter(Boolean))].sort();
    const rmaGroups = [...new Set(results.map(r => r.rmaGroup).filter(Boolean))].sort();
    const rrpTags = [...new Set(results.map(r => r.rrpTag).filter(Boolean))].sort();
    return { countries, languages, rmaGroups, rrpTags };
  }, [results]);

  // Load agents on mount
  React.useEffect(() => {
    getAgents().then(d => setAgents(d.agents || [])).catch(() => {});
  }, []);

  // Pull bad CSAT
  async function handlePull(e) {
    e.preventDefault();
    setPulling(true);
    setError('');
    setTickets([]);
    setResults(null);
    setSummary(null);
    try {
      const data = await csatPull(startDate, endDate, assignee || undefined);
      setTickets(data.tickets || []);
      setSelected(new Set((data.tickets || []).map(t => t.id)));
      setStep('select');
    } catch (err) {
      setError(err.message);
    } finally {
      setPulling(false);
    }
  }

  // Analyze selected tickets
  async function handleAnalyze() {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setAnalyzing(true);
    setError('');
    setProgress({ current: 0, total: ids.length });
    try {
      const data = await csatAnalyzeBatch(ids);
      setResults(data.results || []);
      setSummary(data.summary || null);
      setStep('results');
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  }

  // Export to XLSX
  async function handleExport() {
    if (!results?.length) return;
    setExporting(true);
    try {
      const blob = await csatExport(results, { start: startDate, end: endDate });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CSAT_Review_${startDate}_to_${endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  // Email analysis
  async function handleEmailAnalyze() {
    if (!emailText.trim()) return;
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const data = await csatAnalyzeEmail(emailText);
      setEmailResult(data.analysis);
    } catch (err) {
      setError(err.message);
    } finally {
      setEmailLoading(false);
    }
  }

  function toggleTicket(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const label = (text) => ({
    fontSize: '9px', fontWeight: 700, color: t.textFaint,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    display: 'block', marginBottom: '6px',
  });

  const sectionTitle = (text) => ({
    fontSize: '10px', fontWeight: 700, color: t.textDim,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    display: 'block', marginBottom: '14px',
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 900, color: t.text, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          CSAT Review
        </h1>
        {/* Tab toggle */}
        <div style={{ display: 'flex', gap: '2px', background: t.surfaceElevated, borderRadius: '10px', padding: '2px' }}>
          {[['batch', 'BATCH REVIEW'], ['email', 'EMAIL / PASTE']].map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '6px 16px', fontSize: '10px', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              background: tab === key ? ACCENT : 'transparent',
              color: tab === key ? '#fff' : t.textMuted,
              border: 'none', cursor: 'pointer', borderRadius: '6px',
              transition: 'all 0.15s',
            }}>{lbl}</button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{
          marginBottom: '20px', padding: '12px 16px', borderRadius: '10px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)',
          borderLeft: '2px solid #F07070', color: '#F07070', fontSize: '12px', fontWeight: 600,
        }}>{error}</div>
      )}

      {/* ====== BATCH TAB ====== */}
      {tab === 'batch' && (
        <>
          {/* Pull form */}
          <form onSubmit={handlePull} className="card mb-6">
            <span style={sectionTitle()}>Pull Negative CSAT — Wed to Wed</span>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label style={label()}>From (Wednesday)</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input w-full" />
              </div>
              <div>
                <label style={label()}>To (Wednesday)</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input w-full" />
              </div>
              <div>
                <label style={label()}>Agent Filter</label>
                <select value={assignee} onChange={e => setAssignee(e.target.value)} className="input w-full" style={{ appearance: 'auto' }}>
                  <option value="">All agents</option>
                  {agents.map(a => <option key={a.id} value={a.email}>{a.name}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button type="submit" disabled={pulling} className="btn-primary w-full flex items-center justify-center gap-2">
                  {pulling ? <Spinner size="sm" /> : null}
                  PULL RATINGS
                </button>
              </div>
            </div>
          </form>

          {/* Step: Select tickets */}
          {step === 'select' && tickets.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 900, color: t.text }}>
                    {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} found
                  </span>
                  <button onClick={() => selected.size === tickets.length ? setSelected(new Set()) : setSelected(new Set(tickets.map(t => t.id)))}
                    style={{ fontSize: '10px', color: ACCENT, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {selected.size === tickets.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <button onClick={handleAnalyze} disabled={!selected.size || analyzing} className="btn-primary flex items-center gap-2">
                  {analyzing ? <Spinner size="sm" /> : null}
                  ANALYZE {selected.size > 0 ? `(${selected.size})` : 'SELECTED'}
                </button>
              </div>
              {/* Ticket list with CSAT info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {tickets.map(ticket => (
                  <div key={ticket.id} onClick={() => toggleTicket(ticket.id)} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                    background: selected.has(ticket.id) ? 'rgba(229, 46, 58,0.04)' : t.surfaceCard,
                    border: `1px solid ${selected.has(ticket.id) ? 'rgba(229, 46, 58,0.15)' : t.border}`,
                    transition: 'all 0.1s',
                  }}>
                    <input type="checkbox" checked={selected.has(ticket.id)} readOnly style={{ accentColor: ACCENT }} />
                    <span className="font-mono" style={{ color: ACCENT, fontSize: '12px', fontWeight: 700, minWidth: '70px' }}>
                      #{ticket.id}
                    </span>
                    <span style={{ color: t.text, fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ticket.subject}
                    </span>
                    {ticket.satisfaction?.comment && (
                      <span style={{ color: t.textMuted, fontSize: '11px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        "{ticket.satisfaction.comment}"
                      </span>
                    )}
                    <span style={{ color: t.textDim, fontSize: '11px', minWidth: '80px', textAlign: 'right' }}>
                      {ticket.created_at?.slice(0, 10)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'select' && tickets.length === 0 && !pulling && (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: t.textMuted }}>
              No negative CSAT tickets found for this date range.
            </div>
          )}

          {analyzing && <LoadingOverlay message={`Analyzing ${selected.size} tickets with Ben's taxonomy...`} />}

          {/* Step: Results */}
          {step === 'results' && results && (
            <div>
              {/* Summary bar */}
              {summary && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  {Object.entries(summary.categories || {}).map(([cat, data]) => (
                    <div key={cat} style={{
                      padding: '10px 16px', borderRadius: '10px',
                      background: t.surfaceCard, border: `1px solid ${t.border}`,
                      borderTop: `2px solid ${ACCENT}`,
                      flex: '1 1 120px', minWidth: '120px',
                    }}>
                      <div style={{ fontSize: '22px', fontWeight: 900, color: t.text }}>{data.count}</div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: t.textDim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{cat}</div>
                      {data.subCategories && (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                          {Object.entries(data.subCategories).slice(0, 3).map(([sub, count]) => (
                            <span key={sub} style={{
                              fontSize: '9px', padding: '1px 6px', borderRadius: '6px',
                              background: `${SUB_CAT_COLORS[sub] || '#6B7280'}15`,
                              color: SUB_CAT_COLORS[sub] || '#6B7280',
                              fontWeight: 600,
                            }}>{sub} ({count})</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Executive Summary */}
              {summary?.executiveSummary && (
                <div className="card" style={{ marginBottom: '16px' }}>
                  <span style={sectionTitle()}>Executive Summary</span>
                  <p style={{ color: t.textSecondary, fontSize: '13px', lineHeight: 1.65 }}>{summary.executiveSummary}</p>
                </div>
              )}

              {/* Patterns */}
              {summary?.patterns?.length > 0 && (
                <div className="card" style={{ marginBottom: '16px' }}>
                  <span style={sectionTitle()}>Patterns</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {summary.patterns.map((p, i) => (
                      <div key={i} style={{
                        padding: '10px 14px', borderRadius: '10px',
                        background: 'rgba(255,255,255,0.02)', border: `1px solid ${t.border}`,
                        borderLeft: `2px solid ${ACCENT}`,
                      }}>
                        <p style={{ color: t.text, fontSize: '13px', fontWeight: 700 }}>{p.pattern}</p>
                        <p style={{ color: t.textMuted, fontSize: '12px', marginTop: '3px' }}>{p.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={sectionTitle()}>
                  Ticket Analysis — {results.length} ticket{results.length !== 1 ? 's' : ''}
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => { setStep('select'); setResults(null); setSummary(null); }}
                    className="btn-secondary" style={{ height: '34px', fontSize: '10px', padding: '0 14px' }}>BACK</button>
                  <button onClick={handleExport} disabled={exporting}
                    className="btn-primary" style={{ height: '34px', fontSize: '10px', padding: '0 14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {exporting ? <Spinner size="sm" /> : null}
                    EXPORT XLSX
                  </button>
                </div>
              </div>

              {/* Results table matching Ben's columns */}
              <div className="card" style={{ padding: 0, overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', minWidth: '1100px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                      {['Date', 'Ticket', 'Agent', 'Team', 'Market', 'Country', 'Lang', 'Category', 'Sub-Category', 'Root Cause Analysis', 'Unit', 'RMA', 'RRP'].map(h => (
                        <th key={h} style={{
                          padding: '10px 12px', fontSize: '9px', fontWeight: 700,
                          color: t.textDim, textAlign: 'left',
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          whiteSpace: 'nowrap', position: 'sticky', top: 0,
                          background: t.surfaceCard,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, i) => (
                      <React.Fragment key={r.ticketId || i}>
                        <tr
                          style={{
                            borderBottom: `1px solid ${t.border}`, cursor: 'pointer',
                            background: expandedRow === i ? 'rgba(255,255,255,0.03)' : 'transparent',
                            transition: 'background 0.1s',
                          }}
                          onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                          onMouseEnter={e => { if (expandedRow !== i) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                          onMouseLeave={e => { if (expandedRow !== i) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td style={{ padding: '8px 12px', color: t.textMuted, whiteSpace: 'nowrap' }}>{r.date}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <Link to={`/ticket/${r.ticketId}`} onClick={e => e.stopPropagation()}
                              className="font-mono" style={{ color: ACCENT, textDecoration: 'none', fontWeight: 700, fontSize: '12px' }}>
                              #{r.ticketId}
                            </Link>
                          </td>
                          <td style={{ padding: '8px 12px', color: t.textSecondary, whiteSpace: 'nowrap' }}>{r.agent}</td>
                          <td style={{ padding: '8px 12px', color: t.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>{r.team}</td>
                          <td style={{ padding: '8px 12px', color: t.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>{r.market}</td>
                          <td style={{ padding: '8px 12px', color: t.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>{r.activateCountry || '—'}</td>
                          <td style={{ padding: '8px 12px', color: t.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>{r.language || '—'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <CategoryBadge category={r.internalCategory} />
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {r.subCategory && (
                              <span style={{
                                fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                                background: `${SUB_CAT_COLORS[r.subCategory] || '#6B7280'}12`,
                                color: SUB_CAT_COLORS[r.subCategory] || '#6B7280',
                                fontWeight: 600, whiteSpace: 'nowrap',
                              }}>{r.subCategory}</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', color: t.textSecondary, maxWidth: '350px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {r.rootCauseAnalysis}
                              <svg style={{
                                width: '10px', height: '10px', color: t.textDim, flexShrink: 0,
                                transition: 'transform 0.15s', transform: expandedRow === i ? 'rotate(180deg)' : 'rotate(0deg)',
                              }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', color: t.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>{r.unit}</td>
                        </tr>
                        {expandedRow === i && (
                          <tr style={{ borderBottom: `1px solid ${t.border}`, background: 'rgba(255,255,255,0.03)' }}>
                            <td colSpan={13} style={{ padding: '16px 20px' }}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ fontSize: '12px' }}>
                                <div>
                                  <p style={{ ...label(), marginBottom: '4px' }}>Root Cause Analysis</p>
                                  <p style={{ color: t.textSecondary, lineHeight: 1.6 }}>{r.rootCauseAnalysis}</p>
                                </div>
                                <div>
                                  <p style={{ ...label(), marginBottom: '4px' }}>Customer Comment</p>
                                  <p style={{ color: t.textSecondary, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {r.customerComment || 'No comment provided'}
                                  </p>
                                </div>
                                <div>
                                  <p style={{ ...label(), marginBottom: '4px' }}>Customer Category</p>
                                  <p style={{ color: t.textSecondary }}>{r.customerCategory}</p>
                                </div>
                                <div>
                                  <p style={{ ...label(), marginBottom: '4px' }}>Ticket Type</p>
                                  <p style={{ color: t.textSecondary }}>{r.ticketType || '—'}</p>
                                </div>
                                {r.notes && (
                                  <div className="md:col-span-2">
                                    <p style={{ ...label(), marginBottom: '4px' }}>Notes</p>
                                    <p style={{ color: t.textMuted, lineHeight: 1.5 }}>{r.notes}</p>
                                  </div>
                                )}
                                {r.secondaryCategory && (
                                  <div>
                                    <p style={{ ...label(), marginBottom: '4px' }}>Secondary Category</p>
                                    <CategoryBadge category={r.secondaryCategory} />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ====== EMAIL TAB ====== */}
      {tab === 'email' && (
        <div>
          <div className="card" style={{ marginBottom: '20px' }}>
            <span style={sectionTitle()}>Paste Email or Customer Correspondence</span>
            <p style={{ color: t.textMuted, fontSize: '12px', marginBottom: '14px', lineHeight: 1.5 }}>
              Paste the email content below. SCOUT will classify it using the same taxonomy as batch review.
            </p>
            <textarea
              value={emailText}
              onChange={e => setEmailText(e.target.value)}
              placeholder="Paste email content, screenshot text, or customer correspondence here..."
              style={{
                width: '100%', minHeight: '200px', padding: '12px 14px',
                background: t.surfaceBase, color: t.text, border: `1px solid ${t.border}`,
                borderRadius: '10px', fontSize: '13px', lineHeight: 1.6,
                resize: 'vertical', fontFamily: 'inherit',
              }}
            />
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleEmailAnalyze} disabled={emailLoading || !emailText.trim()}
                className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {emailLoading ? <Spinner size="sm" /> : null}
                ANALYZE EMAIL
              </button>
            </div>
          </div>

          {emailResult && !emailResult.multiple && (
            <div className="card">
              <span style={sectionTitle()}>Analysis Result</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ fontSize: '13px' }}>
                <div>
                  <p style={{ ...label(), marginBottom: '4px' }}>Internal Category</p>
                  <CategoryBadge category={emailResult.internalCategory} />
                </div>
                <div>
                  <p style={{ ...label(), marginBottom: '4px' }}>Sub-Category</p>
                  <span style={{
                    fontSize: '11px', padding: '3px 10px', borderRadius: '6px',
                    background: `${SUB_CAT_COLORS[emailResult.subCategory] || '#6B7280'}15`,
                    color: SUB_CAT_COLORS[emailResult.subCategory] || '#6B7280',
                    fontWeight: 600,
                  }}>{emailResult.subCategory}</span>
                </div>
                <div className="md:col-span-2">
                  <p style={{ ...label(), marginBottom: '4px' }}>Root Cause Analysis</p>
                  <p style={{ color: t.textSecondary, lineHeight: 1.6 }}>{emailResult.rootCauseAnalysis}</p>
                </div>
                <div>
                  <p style={{ ...label(), marginBottom: '4px' }}>Customer Complaint</p>
                  <p style={{ color: t.textSecondary, fontStyle: 'italic' }}>{emailResult.customerComment}</p>
                </div>
                <div>
                  <p style={{ ...label(), marginBottom: '4px' }}>Unit</p>
                  <p style={{ color: t.textSecondary }}>{emailResult.unit || '—'}</p>
                </div>
                {emailResult.suggestedAction && (
                  <div className="md:col-span-2" style={{ borderLeft: `2px solid ${ACCENT}`, paddingLeft: '14px' }}>
                    <p style={{ ...label(), marginBottom: '4px', color: ACCENT }}>Suggested Action</p>
                    <p style={{ color: t.textSecondary, lineHeight: 1.6 }}>{emailResult.suggestedAction}</p>
                  </div>
                )}
                {emailResult.notes && (
                  <div className="md:col-span-2">
                    <p style={{ ...label(), marginBottom: '4px' }}>Notes</p>
                    <p style={{ color: t.textMuted }}>{emailResult.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {emailResult && emailResult.multiple && emailResult.results && (() => {
            const copyTSV = () => {
              const headers = ['Date', 'Ticket', 'Category', 'Sub-Category', 'Root Cause Analysis', 'Customer Comment', 'Unit', 'Agent'];
              const rows = emailResult.results.map(r => [
                r.date || '', r.ticketId || '', r.internalCategory || '', r.subCategory || '',
                r.rootCauseAnalysis || '', r.customerComment || '', r.unit || '', r.agent || ''
              ].map(c => String(c).replace(/\t/g, ' ').replace(/\n/g, ' ')));
              const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
              navigator.clipboard.writeText(tsv);
            };
            const exportCSV = () => {
              const headers = ['Date', 'Ticket', 'Category', 'Sub-Category', 'Root Cause Analysis', 'Customer Comment', 'Unit', 'Agent'];
              const rows = emailResult.results.map(r => [
                r.date || '', r.ticketId || '', r.internalCategory || '', r.subCategory || '',
                r.rootCauseAnalysis || '', r.customerComment || '', r.unit || '', r.agent || ''
              ]);
              const esc = (s) => { const v = String(s); return v.includes(',') || v.includes('"') || v.includes('\n') ? '"' + v.replace(/"/g, '""') + '"' : v; };
              const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'csat-paste-analysis.csv'; a.click();
              URL.revokeObjectURL(url);
            };
            return (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={sectionTitle()}>{emailResult.results.length} Entries Classified</span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={copyTSV} style={{
                    background: 'rgba(229, 46, 58,0.08)', border: '1px solid rgba(229, 46, 58,0.2)',
                    borderRadius: '6px', padding: '5px 12px', color: ACCENT, fontSize: '10px',
                    cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em',
                    fontFamily: 'inherit',
                  }}>COPY FOR SHEETS</button>
                  <button onClick={exportCSV} style={{
                    background: t.surfaceElevated || 'rgba(255,255,255,0.04)', border: `1px solid ${t.border}`,
                    borderRadius: '6px', padding: '5px 12px', color: t.textMuted, fontSize: '10px',
                    cursor: 'pointer', fontWeight: 700, letterSpacing: '0.06em',
                    fontFamily: 'inherit',
                  }}>EXPORT CSV</button>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                    {['Date', 'Ticket', 'Category', 'Sub-Cat', 'Root Cause Analysis', 'Unit', 'Agent'].map(h => (
                      <th key={h} style={{
                        padding: '10px 12px', fontSize: '9px', fontWeight: 700,
                        color: t.textDim, textAlign: 'left',
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        whiteSpace: 'nowrap', background: t.surfaceCard || t.bgCard,
                        position: 'sticky', top: 0,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {emailResult.results.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${t.border}`, verticalAlign: 'top' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 12px', color: t.textMuted, whiteSpace: 'nowrap', fontSize: '11px' }}>{r.date || '\u2014'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {r.ticketId ? (
                          <Link to={`/ticket/${r.ticketId}`} className="font-mono" style={{ color: ACCENT, textDecoration: 'none', fontWeight: 700, fontSize: '12px' }}>
                            #{r.ticketId}
                          </Link>
                        ) : '\u2014'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <CategoryBadge category={r.internalCategory} />
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {r.subCategory && (
                          <span style={{
                            fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                            background: `${SUB_CAT_COLORS[r.subCategory] || '#6B7280'}12`,
                            color: SUB_CAT_COLORS[r.subCategory] || '#6B7280',
                            fontWeight: 600, whiteSpace: 'nowrap',
                          }}>{r.subCategory}</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', color: t.textSecondary, minWidth: '300px', lineHeight: 1.5 }}>
                        {r.rootCauseAnalysis}
                      </td>
                      <td style={{ padding: '10px 12px', color: t.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>{r.unit || '\u2014'}</td>
                      <td style={{ padding: '10px 12px', color: t.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>{r.agent || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
            );
          })()}
          )}
        </div>
      )}
    </div>
  );
}
